import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, XCircle, AlertTriangle, Calendar } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CertificateVerify() {
  const { code } = useParams();
  const { lang, t } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/verify/${code}`);
        setData(res.data);
      } catch {
        setError(t('certVerify.error.fetch'));
      } finally {
        setLoading(false);
      }
    })();
  }, [code, t]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-lg w-full bg-white rounded-2xl p-8 text-center shadow-lg">
          <AlertTriangle size={64} className="text-gray-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-700 mb-2">{t('certVerify.error.notFoundTitle')}</h1>
          <p className="text-gray-500">{t('certVerify.error.notFoundSub')}</p>
        </div>
      </div>
    );
  }

  const isValid = data.valid;
  const isRevoked = data.status === 'revoked';
  const locale = lang === 'en' ? 'en-GB' : 'tr-TR';
  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' }) : '-';

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4" data-testid="verify-page">
      <div className="max-w-lg w-full bg-white rounded-2xl p-8 shadow-xl">
        <div className="text-center mb-6">
          <div className="text-xs font-bold text-blue-600 tracking-widest mb-1">KARIYER ONLINE</div>
          <div className="text-xs text-gray-500">{t('certVerify.subtitle')}</div>
        </div>

        <div className={`rounded-xl p-6 text-center mb-5 ${isValid ? 'bg-green-50 border-2 border-green-200' : 'bg-red-50 border-2 border-red-200'}`}>
          {isValid ? (
            <>
              <CheckCircle size={56} className="text-green-500 mx-auto mb-3" />
              <div className="text-xl font-bold text-green-700" data-testid="verify-valid">{t('certVerify.valid')}</div>
            </>
          ) : (
            <>
              <XCircle size={56} className="text-red-500 mx-auto mb-3" />
              <div className="text-xl font-bold text-red-700" data-testid="verify-revoked">
                {isRevoked ? t('certVerify.revoked') : t('certVerify.invalid')}
              </div>
            </>
          )}
        </div>

        <div className="space-y-3 text-sm">
          <DetailRow label={t('certVerify.student')} value={data.student_name} />
          <DetailRow label={t('certVerify.course')} value={data.course_title} />
          <DetailRow label={t('certVerify.issuedAt')} value={fmtDate(data.issued_at)} icon={<Calendar size={14} />} />
          <DetailRow label={t('certVerify.verificationCode')} value={data.verification_code} mono />
          {isRevoked && (
            <>
              <DetailRow label={t('certVerify.revokedAt')} value={fmtDate(data.revoked_at)} />
              {data.revoke_reason && (
                <div className="bg-red-50 rounded-lg p-3 text-red-700 text-xs">
                  <strong>{t('certVerify.revokeReason')}</strong> {data.revoke_reason}
                </div>
              )}
            </>
          )}
        </div>

        <div className="mt-6 pt-4 border-t text-center text-xs text-gray-400">
          {isValid ? t('certVerify.footer.valid') : t('certVerify.footer.invalid')}
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, value, mono = false, icon = null }) {
  return (
    <div className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
      <div className="text-gray-500 flex items-center gap-2">
        {icon}
        <span>{label}</span>
      </div>
      <div className={`font-bold text-gray-800 ${mono ? 'font-mono' : ''}`}>{value || '-'}</div>
    </div>
  );
}
